import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import RecipeModal from "./components/cookbook/RecipeModal";
import MainLayout from "./layout/MainLayout";
import DashboardOverview from "./components/dashboard/DashboardOverview";
import Cookbook from "./components/cookbook/Cookbook";
import MealPlanner from "./components/mealplanner/MealPlanner";
import SubscriptionHistory from "./components/subscription/SubscriptionHistory";
import Profile from "./components/profile/Profile";
import QuantityConverter from "./components/converter/QuantityConverter";
import { OTPVerificationModal } from "./components/OTPVerificationModal";
import PaymentDialog from "./components/ui/custom/PaymentModal";
import TermsModal from "./components/ui/custom/TermsModal";
import { Recipe, useStore } from "./store/useStore";
import { useEffect, useState } from "react";
import { formatDate } from './lib/dateFormatter';
import { onMessage } from "firebase/messaging";
import { auth, messaging } from './lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

function ProtectedApp() {
    const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
    const user = useStore((state) => state.user);
    const [isLoading, setIsLoading] = useState(true);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const { addNotification, showOnboarding } = useStore();
    const userPreferences = useStore((state) => state.userPreferences);

    const [showPaymentModal, setShowPaymentModal] = useState(!showOnboarding);
    const [showTermModal, setShowTermModal] = useState(!showOnboarding);
    const requestNotificationPermission = useStore((state) => state.requestNotificationPermission);
    const getSubscriptionAmount = useStore((state) => state.getSubscriptionAmount);
    const [couponId, setCouponId] = useState('');
    const [showOTPModal, setShowOTPModal] = useState(true);
    const resendOTP = useStore((state) => state.resendOTP);
    const logout = useStore((state) => state.logout);
    const lastFetch = useStore((state) => state.lastFetch);
    const verifyOTP = useStore((state) => state.verifyOTP);
    const loadPrerequisites = useStore((state) => state.loadPrerequisites);
    const updateUserData = useStore((state) => state.updateUserData);
    const navigate = useNavigate();




    useEffect(() => {
        const fetchToken = async () => {
            try {
                let browserFCMToken = await localStorage.getItem("browserFCMToken");
                if (!browserFCMToken) {
                    requestNotificationPermission();
                } else {
                    if (!user?.FCMTokens?.includes(browserFCMToken)) {
                        requestNotificationPermission();
                    }
                }
            } catch (err) {
                console.error('Error retrieving or sending FCM token:', err);
            }
        };

        fetchToken();
    }, []); // only run once on component mount

    useEffect(() => {

        const handleRecipeOpen = (e: CustomEvent<Recipe>) => {
            setSelectedRecipe(e.detail);
        };
        window.addEventListener('openRecipe', handleRecipeOpen as EventListener);

        return () => {
            window.removeEventListener('openRecipe', handleRecipeOpen as EventListener);
        };
    }, []);

    useEffect(() => {
        if (user?.couponId) {
            setCouponId(user?.couponId);
        }
    }, []);

    useEffect(() => {
        if (!couponId) {
            if (new URLSearchParams(window.location.search).get('coupon_id')) {
                const couponId = new URLSearchParams(window.location.search).get('coupon_id');
                if (couponId) {
                    setCouponId(couponId);
                }
            }
        }
        if (couponId) {
            getSubscriptionAmount(couponId);
        }
    }, [couponId]);

    useEffect(() => {
        if (!user) return;
        if (!lastFetch) {
            loadPrerequisites();
        } else {
            const lastFetch_ = new Date(lastFetch);
            console.log(lastFetch_);
            const now = new Date();
            const nextFetchTime = new Date(lastFetch_.getTime() + 24 * 3600 * 1000); // 24 hours after last fetch

            if (now >= nextFetchTime) {
                loadPrerequisites();
            }
        }

        if (!user.verified) {
            setShowOTPModal(true);
        }
    }, [user, lastFetch]);

    useEffect(() => {
        onMessage(messaging, (payload) => {
            let notification = payload.notification;
            console.log("Foreground message:", payload.notification);
            if (notification?.title && notification.body) {
                addNotification({
                    title: notification.title,
                    message: notification.body,
                    type: 'error'
                });
            }
            // You can show notification UI here
        });
    }, []);

    useEffect(() => {
        updateUserData();
    }, []);

    useEffect(() => {
        if (!user) return;
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setIsLoading(true);
            if (!firebaseUser && isOnline) {
                useStore.setState({ user: null, recipes: [] });
            }
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [isOnline, user]);

    useEffect(() => {
        if (!user?.id) navigate("/login");
    }, [user])


    const handleOTPVerify = async (otp: string) => {
        console.log("Verifying OTP:", otp);

        // verify OTP from store, update user in store!
        // Simulate OTP verification
        await verifyOTP(otp);
    };

    const handleResendOTP = async () => {
        const otpSent = await resendOTP();
        if (otpSent) {
            addNotification({
                type: "success",
                title: "Success",
                message: "New verification code sent!"
            });
        }
        //resent otp from store
    };

    return (
        <>
            {/* Modals & global UI */}
            {user && user.id && !user.verified && <OTPVerificationModal
                open={showOTPModal}
                email={user?.email}
                onVerify={handleOTPVerify}
                onResend={handleResendOTP}
            />}
            {/* Main Layout & Pages */}

            <Routes>
                <Route element={<MainLayout />}>
                    <Route path="/dashboard" element={<DashboardOverview />} />
                    <Route path="/cookbook" element={<Cookbook />} />
                    <Route path="/meal-planner" element={<MealPlanner />} />
                    <Route path="/subscription" element={<SubscriptionHistory />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/converter" element={<QuantityConverter />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
            </Routes>

            {selectedRecipe && (
                <RecipeModal
                    recipe={selectedRecipe}
                    onClose={() => setSelectedRecipe(null)}
                />
            )}
        </>
    );
}

export default ProtectedApp;